
import {useCallback, MouseEvent} from "react";
import {useHistory} from "react-router-dom";
import {UrlBuilder} from "@common-core/runtime-js/util";
import {injectParameters} from "@common-core/runtime-js/support";
import {useRuntime} from "@common-core/react-runtime/context";
import {Metadata} from "../runtime";
import {EntitySourceType} from "../entities";
import {Path} from "./Path";
import {LocationState} from "history";

export type EntityHandler = (
  id: string,
  event?: any,
  currentPath?: string,
  hash?: string
) => void;
export type EventHandler = (event: any) => void;
export type PathHandler = (event: any, path: string) => void;
export type SearchEventHandler = (event: any, query?: string) => void;

// @deprecated
export type DomainObjectEventHandler = (
  event: any,
  id: string,
  entitySourceType?: EntitySourceType,
  currentPath?: string,
  hash?: string
) => void;
export type CreateEntityEventHandler = (
  event: any,
  id: string,
  currentPath?: string
) => void;

export interface Navigation {
  navigateTo: PathHandler;
  navigateToHome: EventHandler;
  navigateToSearch: SearchEventHandler;
  navigateToClientSearch: SearchEventHandler;
  navigateToCreateOperationalEntity: CreateEntityEventHandler;
  navigateToClientEntity: EntityHandler;
  navigateToOperationalEntity: EntityHandler;
  // @deprecated
  navigateToEntityFromEvent: DomainObjectEventHandler;

  // @deprecated
  navigateToEntity: (id: string, entitySourceType: EntitySourceType) => void;
}

export function useNavigation(): Navigation {
  const history = useHistory();
  const runtime = useRuntime<Metadata>();

  const buildRelativeRouteTo = useCallback(
    (path: string, hash?: string): string => {
      return UrlBuilder.builder()
        .withRelative(true)
        .withPathname(path)
        .withSearch(injectParameters(runtime))
        .withHash(hash)
        .build();
    },
    [runtime]
  );

  const navigateTo = useCallback<PathHandler>(
    (event: any, path: string): void => {
      const url = buildRelativeRouteTo(path);
      if (event?.metaKey) {
        window.open(url, "_blank");
      } else {
        history.push(url);
      }
    },
    [history, buildRelativeRouteTo]
  );

  const navigate = useCallback(
    (
      path: Path | string,
      event?: any,
      hash?: string,
      state?: LocationState
    ): void => {
      const url = buildRelativeRouteTo(path, hash);
      if (event?.metaKey) {
        window.open(url, "_blank");
      } else {
        history.push(url, state);
      }
    },
    [history, buildRelativeRouteTo]
  );

  const buildHomeRoute = useCallback((): string => {
    return UrlBuilder.builder()
      .withRelative(true)
      .withPathname("/")
      .withSearch(injectParameters(runtime))
      .build();
  }, [runtime]);

  const navigateToHome = useCallback<EventHandler>(
    (event: any): void => {
      const url = buildHomeRoute();
      if (event?.metaKey) {
        window.open(url, "_blank");
      } else {
        history.push(url);
      }
    },
    [history, buildHomeRoute]
  );

  const buildSearchRoute = useCallback(
    (pathName = "/search"): string => {
      return UrlBuilder.builder()
        .withRelative(true)
        .withPathname(pathName)
        .withSearch(injectParameters(runtime))
        .build();
    },
    [runtime]
  );

  const navigateToSearch = useCallback<SearchEventHandler>(
    (event: any, query?: string): void => {
      const url = buildSearchRoute();
      if (event?.metaKey) {
        window.open(url, "_blank");
      } else {
        history.push(url, {query});
      }
    },
    [history, buildSearchRoute]
  );

  const navigateToClientSearch = useCallback<SearchEventHandler>(
    (event: any, query?: string): void => {
      const url = buildSearchRoute("/client-hierarchy-search");
      if (event?.metaKey) {
        window.open(url, "_blank");
      } else {
        history.push(url, {query});
      }
    },
    [history, buildSearchRoute]
  );

  const buildEntityRoute = useCallback(
    (
      id: string,
      entitySourceType: EntitySourceType,
      hash = "overview"
    ): string => {
      // TODO: Is there a better way to provide paths?
      let pathname: string;
      switch (entitySourceType) {
        case EntitySourceType.CLIENT_HIERARCHY:
          pathname = "/client-hierarchy";
          break;
        case EntitySourceType.OLD:
        case EntitySourceType.OPERATIONAL:
        default:
          pathname = "/entity";
      }

      const url = UrlBuilder.builder()
        .withRelative(true)
        .withPathname(`${pathname}/${id}`)
        .withSearch(injectParameters(runtime))
        .withHash(hash)
        .build();
      console.log(`Navigating to ${url}`);
      return url;
    },
    [runtime]
  );

  const buildCreateRoute = useCallback(
    (pathname: string, id: string): string => {
      const url = UrlBuilder.builder()
        .withRelative(true)
        .withPathname(`${pathname}/${id}`)
        .withSearch(injectParameters(runtime))
        .build();
      console.log(`Navigating to ${url}`);
      return url;
    },
    [runtime]
  );

  const navigateToOperationalEntity = useCallback<EntityHandler>(
    (
      id: string,
      event?: MouseEvent,
      currentPath?: string,
      hash = "overview"
    ): void => {
      navigate(
        `${Path.OPERATIONAL_ENTITY}/${id}`,
        event,
        hash,
        currentPath
          ? {
              from: currentPath
            }
          : undefined
      );
    },
    [navigate]
  );

  const navigateToClientEntity = useCallback<EntityHandler>(
    (
      id: string,
      event?: MouseEvent,
      currentPath?: string,
      hash = "overview"
    ): void => {
      navigate(`${Path.CLIENT_HIERARCHY}/${id}`, event, hash, {
        from: currentPath
      });
    },
    [navigate]
  );

  // @deprecated - use navigateToOperationalEntity or navigateToClientEntity instead
  const navigateToEntityFromEvent = useCallback<DomainObjectEventHandler>(
    (
      event: MouseEvent<Element, MouseEvent>,
      id: string,
      entitySourceType = EntitySourceType.OPERATIONAL,
      currentPath?: string,
      hash?: string
    ): void => {
      const url = buildEntityRoute(id, entitySourceType, hash);
      if (event?.metaKey) {
        window.open(url, "_blank");
      } else {
        history.push(url, {from: currentPath});
      }
    },
    [history, buildEntityRoute]
  );

  // @deprecated - use navigateToOperationalEntity or navigateToClientEntity instead
  const navigateToEntity = useCallback<
    (id: string, entitySourceType: EntitySourceType) => void
  >(
    (id, entitySourceType = EntitySourceType.OPERATIONAL): void => {
      const url = buildEntityRoute(id, entitySourceType);
      window.open(url, "_blank");
    },
    [buildEntityRoute]
  );

  const navigateToCreateOperationalEntity =
    useCallback<CreateEntityEventHandler>(
      (event: any, id: string, currentPath?: string): void => {
        const url = buildCreateRoute("/create-operational-entity", id);
        if (event?.metaKey) {
          window.open(url, "_blank");
        } else {
          history.push(url, {from: currentPath});
        }
      },
      [history, buildCreateRoute]
    );

  return {
    navigateTo,
    navigateToHome,
    navigateToSearch,
    navigateToClientSearch,
    navigateToClientEntity,
    navigateToOperationalEntity,
    navigateToEntityFromEvent,
    navigateToEntity,
    navigateToCreateOperationalEntity
  };
}
